// ==================== TERMS OF SERVICE PAGE ====================

import { motion } from 'framer-motion';
import { 
  FileText, 
  CheckCircle2, 
  AlertTriangle, 
  Shield, 
  CreditCard, 
  Ban, 
  Scale, 
  Mail,
  Users,
  Globe,
  Gavel
} from 'lucide-react';
import { fadeInUp, staggerContainer } from '../../utils/animations';

// Section component for consistent styling
const Section = ({ 
  icon: Icon, 
  title, 
  children 
}: { 
  icon: React.ElementType; 
  title: string; 
  children: React.ReactNode;
}) => (
  <motion.section 
    className="mb-10"
    variants={fadeInUp}
  >
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 rounded-xl bg-(--color-orange-glow) flex items-center justify-center">
        <Icon size={20} className="text-(--color-signal-orange)" />
      </div>
      <h2 className="text-xl font-heading font-semibold text-white">{title}</h2>
    </div>
    <div className="text-(--color-soft-gray) leading-relaxed space-y-4 pl-13">
      {children}
    </div>
  </motion.section>
);

const TermsPage = () => {
  const lastUpdated = "December 19, 2025";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero Section */}
      <section className="relative py-16 lg:py-20 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-linear-to-b from-(--color-midnight-navy) via-(--color-dark-navy) to-(--color-midnight-navy)" />
        <motion.div 
          className="absolute top-1/4 right-1/4 w-[400px] h-[400px] bg-(--color-signal-orange)/10 rounded-full blur-3xl"
          animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity }}
        />

        <div className="container-custom relative z-10">
          <motion.div 
            className="text-center max-w-3xl mx-auto"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div 
              className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-(--color-orange-glow) mb-6"
              variants={fadeInUp}
            >
              <FileText size={32} className="text-(--color-signal-orange)" />
            </motion.div>
            
            <motion.h1 
              className="text-4xl lg:text-5xl font-heading font-bold text-white mb-4"
              variants={fadeInUp}
            >
              Terms of Service
            </motion.h1>
            
            <motion.p 
              className="text-lg text-(--color-soft-gray) mb-4"
              variants={fadeInUp}
            >
              Please read these terms carefully before using our platform. 
              By using AmplifyMsg, you agree to be bound by these terms.
            </motion.p>

            <motion.p 
              className="text-sm text-(--color-muted-gray)"
              variants={fadeInUp}
            >
              Last updated: {lastUpdated}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12 lg:py-16">
        <div className="container-custom">
          <motion.div 
            className="max-w-4xl mx-auto bg-(--color-charcoal-black)/50 rounded-2xl p-8 lg:p-12 border border-(--color-border-gray)"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {/* Agreement */}
            <Section icon={CheckCircle2} title="Agreement to Terms">
              <p>
                These Terms of Service ("Terms") govern your access to and use of the 
                AmplifyMsg platform, including any content, functionality, and services 
                offered on or through amplifymsg.com (the "Platform").
              </p>
              <p>
                By creating an account or using the Platform, you accept and agree to be 
                bound by these Terms and our Privacy Policy. If you do not agree to these 
                Terms, you must not access or use the Platform.
              </p>
              <p>
                We reserve the right to change or modify these Terms at any time. Your 
                continued use of the Platform following any changes constitutes acceptance 
                of those changes.
              </p>
            </Section>

            {/* Description of Service */}
            <Section icon={Globe} title="Description of Service">
              <p>
                AmplifyMsg is a cloud-based Software-as-a-Service (SaaS) platform that 
                provides businesses with WhatsApp Business API integration, messaging 
                automation, campaign management, and customer communication tools.
              </p>
              <p className="font-medium text-white mt-4">Our services include:</p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
                <li>WhatsApp Business API integration and management</li>
                <li>Contact management and segmentation</li>
                <li>Bulk messaging and campaign creation</li>
                <li>Automated messaging workflows</li>
                <li>Analytics and reporting</li>
                <li>Template management</li>
                <li>Live chat functionality</li>
              </ul>
            </Section>

            {/* Account Terms */}
            <Section icon={Users} title="Account Registration">
              <p>
                To use AmplifyMsg, you must create an account. When registering, you agree to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-4">
                <li>Provide accurate, current, and complete information</li>
                <li>Maintain and update your information to keep it accurate</li>
                <li>Maintain the security of your account credentials</li>
                <li>Accept responsibility for all activities under your account</li>
                <li>Notify us immediately of any unauthorized use</li>
              </ul>
              <p className="mt-4">
                You must be at least 18 years old and have the legal authority to bind 
                your organization to these Terms. You may not share your account with 
                others or allow others to access your account.
              </p>
            </Section>

            {/* Subscription and Payment */}
            <Section icon={CreditCard} title="Subscription and Payment">
              <p className="font-medium text-white">Subscription Plans</p>
              <p>
                AmplifyMsg offers various subscription plans with different features and 
                limits. Details of each plan are available on our pricing page. You agree 
                to pay all fees associated with your selected plan.
              </p>

              <p className="font-medium text-white mt-6">Payment Terms</p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
                <li>Subscription fees are billed in advance on a monthly or annual basis</li>
                <li>All fees are non-refundable unless otherwise specified</li>
                <li>We may change our pricing with 30 days' notice</li>
                <li>You are responsible for all applicable taxes</li>
              </ul>

              <p className="font-medium text-white mt-6">Cancellation</p>
              <p>
                You may cancel your subscription at any time. Upon cancellation, you will 
                retain access until the end of your current billing period. No refunds are 
                provided for partial billing periods.
              </p>

              <p className="font-medium text-white mt-6">Setup Fees</p>
              <p>
                Optional setup and integration services may incur additional fees. These 
                fees will be clearly communicated and agreed upon before any work begins.
              </p>
            </Section>

            {/* Acceptable Use */}
            <Section icon={CheckCircle2} title="Acceptable Use Policy">
              <p>
                You agree to use AmplifyMsg only for lawful purposes and in accordance 
                with these Terms. You agree not to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-4">
                <li>Send unsolicited messages (spam) or violate anti-spam laws</li>
                <li>Send messages without proper consent from recipients</li>
                <li>Impersonate any person or entity</li>
                <li>Transmit harmful, offensive, or illegal content</li>
                <li>Attempt to gain unauthorized access to any systems</li>
                <li>Interfere with or disrupt the Platform's operation</li>
                <li>Use the Platform for any illegal or fraudulent activity</li>
                <li>Violate WhatsApp's Business Policy or Terms of Service</li>
                <li>Resell or redistribute access to the Platform</li>
              </ul>
            </Section>

            {/* Prohibited Uses */}
            <Section icon={Ban} title="Prohibited Content">
              <p>
                You may not use AmplifyMsg to send messages containing:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-4">
                <li>Illegal or harmful content</li>
                <li>Hate speech or discriminatory content</li>
                <li>Violent or threatening content</li>
                <li>Sexually explicit material</li>
                <li>Content that infringes intellectual property rights</li>
                <li>Malware, viruses, or malicious code</li>
                <li>Misleading or deceptive information</li>
                <li>Content promoting illegal activities</li>
              </ul>
              <p className="mt-4">
                We reserve the right to suspend or terminate accounts that violate these 
                prohibitions without notice or refund.
              </p>
            </Section>

            {/* WhatsApp Compliance */}
            <Section icon={Shield} title="WhatsApp Business Compliance">
              <p>
                As a user of WhatsApp Business API through AmplifyMsg, you must comply 
                with all WhatsApp Business policies, including:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-4">
                <li>WhatsApp Business Policy</li>
                <li>WhatsApp Commerce Policy</li>
                <li>WhatsApp Business Messaging Policy</li>
                <li>Meta Business Platform Terms</li>
              </ul>
              <p className="mt-4">
                Violation of WhatsApp policies may result in suspension of your WhatsApp 
                Business account by Meta, for which AmplifyMsg bears no responsibility.
              </p>
            </Section>

            {/* Intellectual Property */}
            <Section icon={FileText} title="Intellectual Property">
              <p className="font-medium text-white">Our Intellectual Property</p>
              <p>
                The Platform, including all content, features, and functionality, is owned 
                by AmplifyMsg and protected by copyright, trademark, and other intellectual 
                property laws. You may not copy, modify, distribute, or create derivative 
                works without our express written consent.
              </p>

              <p className="font-medium text-white mt-6">Your Content</p>
              <p>
                You retain ownership of content you upload to the Platform. By uploading 
                content, you grant us a license to use, store, and process that content 
                as necessary to provide our services.
              </p>
            </Section>

            {/* Limitation of Liability */}
            <Section icon={AlertTriangle} title="Limitation of Liability">
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, AMPLIFYMSG SHALL NOT BE LIABLE 
                FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, 
                INCLUDING BUT NOT LIMITED TO:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-4">
                <li>Loss of profits, revenue, or business opportunities</li>
                <li>Loss of data or data breach</li>
                <li>Service interruptions or downtime</li>
                <li>Actions taken by WhatsApp or Meta</li>
                <li>Third-party claims arising from your use</li>
              </ul>
              <p className="mt-4">
                Our total liability for any claims arising from these Terms shall not 
                exceed the amount you paid us in the twelve months preceding the claim.
              </p>
            </Section>

            {/* Disclaimer */}
            <Section icon={AlertTriangle} title="Disclaimer of Warranties">
              <p>
                THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES 
                OF ANY KIND, WHETHER EXPRESS OR IMPLIED. WE DISCLAIM ALL WARRANTIES, 
                INCLUDING BUT NOT LIMITED TO:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-4">
                <li>MERCHANTABILITY</li>
                <li>FITNESS FOR A PARTICULAR PURPOSE</li>
                <li>NON-INFRINGEMENT</li>
                <li>ACCURACY OR COMPLETENESS OF INFORMATION</li>
              </ul>
              <p className="mt-4">
                We do not warrant that the Platform will be uninterrupted, error-free, 
                or free of viruses or other harmful components.
              </p>
            </Section>

            {/* Indemnification */}
            <Section icon={Shield} title="Indemnification">
              <p>
                You agree to indemnify, defend, and hold harmless AmplifyMsg and its 
                officers, directors, employees, and agents from any claims, damages, 
                losses, liabilities, and expenses (including legal fees) arising from:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-4">
                <li>Your use of the Platform</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any third-party rights</li>
                <li>Your content or messages sent through the Platform</li>
                <li>Any claims by recipients of your messages</li>
              </ul>
            </Section>

            {/* Termination */}
            <Section icon={Ban} title="Termination">
              <p>
                We may terminate or suspend your account immediately, without prior notice 
                or liability, for any reason, including:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-4">
                <li>Breach of these Terms</li>
                <li>Violation of applicable laws</li>
                <li>Non-payment of fees</li>
                <li>Fraudulent or illegal activity</li>
                <li>At our sole discretion</li>
              </ul>
              <p className="mt-4">
                Upon termination, your right to use the Platform will immediately cease. 
                You may export your data within 30 days of termination.
              </p>
            </Section>

            {/* Governing Law */}
            <Section icon={Gavel} title="Governing Law">
              <p>
                These Terms shall be governed by and construed in accordance with the 
                laws of the State of California, United States, without regard to its 
                conflict of law provisions.
              </p>
              <p className="mt-4">
                Any disputes arising from these Terms shall be resolved through binding 
                arbitration in San Francisco, California, in accordance with the rules 
                of the American Arbitration Association.
              </p>
            </Section>

            {/* Severability */}
            <Section icon={Scale} title="Severability">
              <p>
                If any provision of these Terms is found to be unenforceable or invalid, 
                that provision will be limited or eliminated to the minimum extent necessary, 
                and the remaining provisions will remain in full force and effect.
              </p>
            </Section>

            {/* Entire Agreement */}
            <Section icon={FileText} title="Entire Agreement">
              <p>
                These Terms, together with our Privacy Policy, constitute the entire 
                agreement between you and AmplifyMsg regarding the use of the Platform 
                and supersede any prior agreements or understandings.
              </p>
            </Section>

            {/* Contact */}
            <Section icon={Mail} title="Contact Us">
              <p>
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="mt-4 p-4 bg-(--color-navy-light) rounded-xl">
                <p className="font-medium text-white">AmplifyMsg Legal Team</p>
                <p>Email: legal@amplifymsg.com</p>
                <p>Address: San Francisco, CA, USA</p>
              </div>
            </Section>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export { TermsPage };

